// client/src/contexts/FavoritesContext.js
// ================================================================
// FINAL FIX: Uses centralized favoritesAPI service
// Solves: Port 3000 404s & Token Handling
// ================================================================

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
// ✅ ΔΙΟΡΘΩΣΗ: Import από το κεντρικό service, όχι από axios/config
import { favoritesAPI } from '../services/api'; 

const FavoritesContext = createContext();

export const FavoritesProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [favorites, setFavorites] = useState([]); // Αποθηκεύει μόνο τα Product IDs
  const [loading, setLoading] = useState(true);

  // ✅ Fetch ALL favorites ONCE when user logs in
  useEffect(() => {
    // Αν δεν είναι συνδεδεμένος ή δεν είναι επιχείρηση, καθαρισμός
    if (!isAuthenticated || user?.role !== 'business') {
      setFavorites([]);
      setLoading(false);
      return;
    }

    const fetchFavorites = async () => {
      try {
        // ✅ Χρήση της κεντρικής μεθόδου (λύνει το θέμα Port 3000)
        // Το token μπαίνει αυτόματα από το api.js
        const response = await favoritesAPI.getAll();
        
        // Extract just the product IDs for quick lookup in UI
        const favoriteIds = response.data.favorites.map(
          fav => fav.productId?._id || fav.productId
        );
        setFavorites(favoriteIds);
      } catch (error) {
        console.error('Error fetching favorites:', error);
        setFavorites([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [isAuthenticated, user?.role]);

  // ✅ Add to favorites
  const addFavorite = async (productId) => {
    try {
      // ✅ Κεντρική κλήση API
      await favoritesAPI.add(productId);
      
      // Update local state immediately (Optimistic UI)
      setFavorites(prev => [...prev, productId]);
      return { success: true };
    } catch (error) {
      console.error('Error adding favorite:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to add favorite' 
      };
    }
  };

  // ✅ Remove from favorites
  const removeFavorite = async (productId) => {
    try {
      // ✅ Κεντρική κλήση API
      await favoritesAPI.remove(productId);
      
      // Update local state immediately
      setFavorites(prev => prev.filter(id => id !== productId));
      return { success: true };
    } catch (error) {
      console.error('Error removing favorite:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to remove favorite' 
      };
    }
  };

  // ✅ Check if product is favorited (instant - no API call!)
  const isFavorited = (productId) => {
    return favorites.includes(productId);
  };

  const value = {
    favorites,
    loading,
    isFavorited,
    addFavorite,
    removeFavorite,
    favoritesCount: favorites.length
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within FavoritesProvider');
  }
  return context;
};